import React from 'react';
import { 
  ShoppingCart, 
  Wrench, 
  Shield, 
  Package, 
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';

/**
 * Icon mapping for different ticket types
 */
const TICKET_TYPE_ICONS = {
  ORDER: {
    icon: ShoppingCart,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    label: 'Order'
  },
  REPAIR: {
    icon: Wrench,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    label: 'Repair'
  },
  WARRANTY: {
    icon: Shield,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    label: 'Warranty'
  }
};

/**
 * Icon mapping for meta statuses
 */
const META_STATUS_ICONS = {
  NEW: {
    icon: AlertCircle,
    color: 'text-green-600',
    label: 'New'
  },
  IN_PROGRESS: {
    icon: Clock,
    color: 'text-blue-600',
    label: 'In Progress'
  },
  WAITING: {
    icon: Package,
    color: 'text-orange-600',
    label: 'Waiting'
  },
  DONE: {
    icon: CheckCircle,
    color: 'text-gray-600',
    label: 'Done'
  }
};

/**
 * TicketTypeIcon Component
 * Displays icon for different ticket types with proper styling
 * 
 * @param {Object} props
 * @param {'ORDER'|'REPAIR'|'WARRANTY'} props.type - The ticket type
 * @param {'xs'|'sm'|'md'|'lg'|'xl'} props.size - Icon size (default: 'md')
 * @param {boolean} props.showBackground - Whether to show background circle (default: true)
 * @param {boolean} props.showLabel - Whether to show text label (default: false)
 * @param {string} props.className - Additional CSS classes
 */
const TicketTypeIcon = ({ 
  type, 
  size = 'md', 
  showBackground = true, 
  showLabel = false,
  className = '' 
}) => {
  const config = TICKET_TYPE_ICONS[type];
  
  if (!config) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <XCircle className="text-red-500" size={16} />
        {showLabel && <span className="text-sm text-red-500">Unknown</span>}
      </div>
    );
  }
  
  const IconComponent = config.icon;
  
  const sizeClasses = {
    xs: { icon: 12, container: 'w-6 h-6', text: 'text-xs' },
    sm: { icon: 14, container: 'w-7 h-7', text: 'text-sm' },
    md: { icon: 16, container: 'w-8 h-8', text: 'text-sm' },
    lg: { icon: 20, container: 'w-10 h-10', text: 'text-base' },
    xl: { icon: 24, container: 'w-12 h-12', text: 'text-lg' }
  };
  
  const sizeConfig = sizeClasses[size];
  
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {showBackground ? (
        <div className={`
          ${sizeConfig.container} 
          ${config.bgColor} 
          rounded-full 
          flex 
          items-center 
          justify-center
        `}>
          <IconComponent 
            size={sizeConfig.icon} 
            className={config.color}
          />
        </div>
      ) : (
        <IconComponent 
          size={sizeConfig.icon} 
          className={config.color}
        />
      )}
      
      {showLabel && (
        <span className={`${sizeConfig.text} font-medium ${config.color}`}>
          {config.label}
        </span>
      )}
    </div>
  );
};

/**
 * MetaStatusIcon Component
 * Displays icon for meta statuses
 * 
 * @param {Object} props
 * @param {'NEW'|'IN_PROGRESS'|'WAITING'|'DONE'} props.status - The meta status
 * @param {'xs'|'sm'|'md'|'lg'|'xl'} props.size - Icon size (default: 'sm')
 * @param {boolean} props.showLabel - Whether to show text label (default: false)
 * @param {string} props.className - Additional CSS classes
 */
export const MetaStatusIcon = ({ 
  status, 
  size = 'sm', 
  showLabel = false,
  className = '' 
}) => {
  const config = META_STATUS_ICONS[status];
  
  if (!config) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <XCircle className="text-red-500" size={12} />
        {showLabel && <span className="text-xs text-red-500">Unknown</span>}
      </div>
    );
  }
  
  const IconComponent = config.icon;
  
  const sizeClasses = {
    xs: { icon: 10, text: 'text-xs' },
    sm: { icon: 12, text: 'text-xs' },
    md: { icon: 14, text: 'text-sm' },
    lg: { icon: 16, text: 'text-sm' },
    xl: { icon: 20, text: 'text-base' }
  };
  
  const sizeConfig = sizeClasses[size];
  
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <IconComponent 
        size={sizeConfig.icon} 
        className={config.color}
      />
      
      {showLabel && (
        <span className={`${sizeConfig.text} ${config.color}`}>
          {config.label}
        </span>
      )}
    </div>
  );
};

/**
 * TicketTypeSelect Component
 * Dropdown for selecting ticket types with icons
 */
export const TicketTypeSelect = ({ value, onChange, placeholder = "Select ticket type", className = '' }) => {
  const options = Object.entries(TICKET_TYPE_ICONS).map(([key, config]) => ({
    value: key,
    label: config.label,
    icon: config.icon,
    color: config.color
  }));
  
  return (
    <select 
      value={value || ''} 
      onChange={(e) => onChange(e.target.value)}
      className={`
        flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md 
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
        ${className}
      `}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

/**
 * Get ticket type configuration
 */
export const getTicketTypeConfig = (type) => {
  return TICKET_TYPE_ICONS[type] || null;
};

/**
 * Get all ticket type options
 */
export const getTicketTypeOptions = () => {
  return Object.entries(TICKET_TYPE_ICONS).map(([key, config]) => ({
    value: key,
    label: config.label,
    icon: config.icon,
    color: config.color
  }));
};

/**
 * Get meta status configuration
 */
export const getMetaStatusConfig = (status) => {
  return META_STATUS_ICONS[status] || null;
};

export { TicketTypeIcon as default };
export { TicketTypeIcon };
